import { BranchesOutlined, ImportOutlined, PlusOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { useMutation, useQuery, useSuspenseQuery } from '@apollo/client'
import {
  FetchOrganizationSettingDocument,
  FetchUserGroupsDocument,
  type FetchUserGroupsQuery,
  UpsertUserGroupsDocument,
  type UserGroup,
  type UserGroupInput,
} from '@salescore/client-api'
import { Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import { getOrganizationIdFromPath, HandleQuery } from '@salescore/client-common'
import { UpsertUserGroupForm, UserGroupCategoryNamesForm, UserGroupsTree } from '@salescore/client-feature-setting'
// eslint-disable-next-line sonarjs/deprecation
import { CsvDownloadButton, useModal } from '@salescore/frontend-common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button, message, Modal } from 'antd'
import { diff } from 'deep-object-diff'
import { Array, Order } from 'effect'
import { type ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** UserGroups を rank でソート */
const sortUserGroupsByRank: (self: FetchUserGroupsQuery['userGroups']) => FetchUserGroupsQuery['userGroups'] =
  Array.sortBy(Order.mapInput(Order.Number, (x) => x.rank))

const Page = (): ReactNode => {
  // eslint-disable-next-line sonarjs/deprecation
  const createModal = useModal()
  const { organizationId } = Route.useParams()
  const { t } = useTranslation()
  // eslint-disable-next-line sonarjs/deprecation
  const userGroupCategoryNamesModal = useModal()
  const {
    data: { userGroups: savedUserGroups, organization },
    refetch,
  } = useSuspenseQuery(FetchUserGroupsDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
    fetchPolicy: 'cache-and-network',
  })
  /** fetch した UserGroups を rank でソートして、memo化（変更の破棄時に参照）*/
  const sortedSavedUserGroups = useMemo(() => sortUserGroupsByRank(savedUserGroups), [savedUserGroups])
  const [userGroups, setUserGroups] = useState(sortedSavedUserGroups)
  const [parentUserGroupToUpdate, setParentUserGroupToUpdate] = useState<UserGroup | null>(null)
  // 作成、編集、削除は直後に refetch されるので、この変数は基本的にソートのみを検知する
  const isUserGroupsSorted = useMemo(() => {
    const userGroupsDiff = diff(sortedSavedUserGroups, userGroups)
    return Object.keys(userGroupsDiff).length > 0
  }, [sortedSavedUserGroups, userGroups])
  const [upsertUserGroupsMutation, { loading: upsertUserGroupsMutationLoading }] = useMutation(
    UpsertUserGroupsDocument,
    {
      refetchQueries: ['fetchUserGroups'],
    },
  )
  const fetchOrganizationSettingQuery = useQuery(FetchOrganizationSettingDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
    },
  })
  const updateUserGroupsSort = ({
    afterComplete,
    afterError,
  }: {
    afterComplete?: () => void
    afterError?: () => void
  }): void => {
    const newUserGroups = userGroups
      .filter((x) => x.id !== 'no-team')
      .map((userGroup, index): UserGroupInput => ({
        id: userGroup.id,
        name: userGroup.name,
        depth: userGroup.depth,
        rank: index + 1,
        user_group_id: userGroup.user_group_id ?? null, // undefinedだと削除時にGraphQLリクエストされない
        userIds: userGroup.users.map((x) => x.id),
      }))
    void upsertUserGroupsMutation({
      variables: {
        organizationId: getOrganizationIdFromPath(),
        userGroups: newUserGroups,
      },
      onCompleted: ({ upsertUserGroups }) => {
        void fetchOrganizationSettingQuery.refetch()
        afterComplete?.()
        // sortedSavedUserGroups 同様、rank でソートして userGroups を更新
        setUserGroups(sortUserGroupsByRank(upsertUserGroups))
      },
      onError: () => {
        afterError?.()
      },
    })
  }
  const onSave = (): void => {
    updateUserGroupsSort({
      afterComplete: () => {
        void message.success(t(`更新しました`))
        Posthog.track(POSTHOG_EVENTS.save_user_groups, { organizationId: getOrganizationIdFromPath() })
      },
      afterError: () => {
        void message.error(t(`エラーが発生しました`))
      },
    })
  }
  const onCreateChildNode = (userGroup: UserGroup) => {
    setParentUserGroupToUpdate(userGroup)
    createModal.showModal()
  }
  // onAfterFinish で refetch されるので、並び替えの変更があった場合は事前に保存する
  const onUpdateUserGroupSort = (): void => {
    if (!isUserGroupsSorted) {
      return
    }
    updateUserGroupsSort({})
  }
  const onAfterFinish = async (): Promise<void> => {
    const {
      data: { userGroups },
    } = await refetch()
    createModal.hideModal()
    setUserGroups(userGroups)
    setParentUserGroupToUpdate(null)
  }
  const onCancel = () => {
    createModal.hideModal()
    setParentUserGroupToUpdate(null)
  }
  const navigate = useNavigate()

  return (
    <div>
      <header className="sticky top-0 z-10 bg-[#F9FBFD]">
        <PageHeader
          title={t(`グループ設定`)}
          extra={[
            // CSVインポート側のcsv出力と合わせること（微妙に違いがあると紛らわしいため）
            <CsvDownloadButton
              key="button-csv-download"
              data={savedUserGroups.map((x) => [x.id, x.user_group_id, x.name])}
              filename={t(`グループ一覧`)}
              headers={[t(`グループID(user_group_id)`), t(`親グループID`), t(`グループ名`)]}
            />,
            <Button
              key="csv"
              icon={<ImportOutlined />}
              type="default"
              onClick={() => {
                void navigate({ to: '/o/$organizationId/v2/settings/user_groups/csv', params: { organizationId } })
              }}
            >
              {t(`CSV一括設定`)}
            </Button>,
            <Button
              key="button-set-hierarchy"
              icon={<BranchesOutlined />}
              type="default"
              onClick={userGroupCategoryNamesModal.showModal}
            >
              {t(`階層構造の設定`)}
            </Button>,
            <Button key="button-create" icon={<PlusOutlined />} type="primary" onClick={createModal.showModal}>
              {t(`作成`)}
            </Button>,
            <Button
              key="button-discard"
              disabled={!isUserGroupsSorted || upsertUserGroupsMutationLoading}
              danger
              onClick={() => {
                // 変更を破棄: userGroupsをsortedSavedUserGroupsの並び順でリセット
                setUserGroups(sortedSavedUserGroups)
              }}
            >
              {t('変更を破棄')}
            </Button>,
            <Button
              key="button-save"
              type="primary"
              disabled={!isUserGroupsSorted || upsertUserGroupsMutationLoading}
              loading={upsertUserGroupsMutationLoading}
              onClick={onSave}
            >
              {t(`保存`)}
            </Button>,
          ]}
        />
      </header>
      <UserGroupsTree
        userGroups={userGroups}
        onUpdateUserGroupSort={onUpdateUserGroupSort}
        onCreateChildNode={onCreateChildNode}
        onAfterFinish={onAfterFinish}
        onSort={setUserGroups}
        userGroupCategoryNames={organization.setting.userGroupCategoryNames}
      />
      <Modal
        key="createModal"
        open={createModal.isModalVisible}
        onCancel={onCancel}
        width={'70%'}
        cancelText={t(`閉じる`)}
        okButtonProps={{ style: { display: 'none' } }}
        style={{ top: '3%' }}
      >
        <UpsertUserGroupForm
          onUpdateUserGroupSort={onUpdateUserGroupSort}
          onAfterFinish={onAfterFinish}
          allUsers={savedUserGroups.flatMap((team) => team.users.map((user) => ({ user, team })))}
          allUserGroups={savedUserGroups}
          parentUserGroup={parentUserGroupToUpdate}
        />
      </Modal>
      <Modal
        key="userGroupCategoryNamesModal"
        open={userGroupCategoryNamesModal.isModalVisible}
        onCancel={userGroupCategoryNamesModal.hideModal}
        width={'70%'}
        cancelText={t(`閉じる`)}
        okButtonProps={{ style: { display: 'none' } }}
        style={{ top: '3%' }}
      >
        <HandleQuery>
          <UserGroupCategoryNamesForm
            onAfterFinish={() => {
              userGroupCategoryNamesModal.hideModal()
            }}
            userGroups={savedUserGroups}
          />
        </HandleQuery>
      </Modal>
    </div>
  )
}

export const Route = createFileRoute('/o/$organizationId/v2/settings/user_groups/')({
  component: Page,
})
